/**
 * #580 — version-skew guard.
 *
 * On boot and resume, compare the running frontend_git_sha to live /version.json.
 * Mismatch → one cache-busted reload (never a loop). Offline / empty SHA → silent.
 */

import { diagnosticLog } from './diagnosticLogger';
import { cacheBustedReload } from './bootRecovery';
import { markInAppNavigationReloadPending } from '../transport/transportPageTeardown';
import {
  bundleShaMatches,
  fetchRemoteVersion,
  getWebBundleMeta,
  type RemoteVersionInfo,
} from './webBundleMeta';
import { shouldDeferBundleSkewReloadForPlayback } from './bundleSkewReloadPlaybackGate';

export const BUNDLE_SKEW_RELOAD_KEY = 'maestro_bundle_skew_reloaded';
export const BUNDLE_SKEW_BEACON_KEY = 'maestro_bundle_skew_beacon';

export type BundleSkewResult = 'matched' | 'offline' | 'reloaded' | 'bounded';

export interface BundleSkewDeps {
  runningSha: string;
  fetchRemote: () => Promise<RemoteVersionInfo | null>;
  storage: Pick<Storage, 'getItem' | 'setItem'>;
  reload: () => void;
  emitBeacon?: (running: string, current: string, extra?: Record<string, unknown>) => void;
}

function remoteFrontendSha(remote: RemoteVersionInfo): string {
  return String(remote.frontend_git_sha || remote.sha || remote.gitHash || '').trim();
}

export function emitBundleSkewDetectedBeacon(
  running: string,
  current: string,
  extra: Record<string, unknown> = {},
): void {
  try {
    diagnosticLog('warn', 'js:render', 'bundle_skew_detected', {
      running,
      current,
      ...extra,
    });
  } catch {
    /* never propagate */
  }
}

function beaconAlreadyEmitted(
  storage: Pick<Storage, 'getItem'>,
  running: string,
  current: string,
): boolean {
  try {
    return storage.getItem(BUNDLE_SKEW_BEACON_KEY) === `${running}|${current}`;
  } catch {
    return false;
  }
}

function markBeaconEmitted(
  storage: Pick<Storage, 'setItem'>,
  running: string,
  current: string,
): void {
  try {
    storage.setItem(BUNDLE_SKEW_BEACON_KEY, `${running}|${current}`);
  } catch {
    /* ignore */
  }
}

/** True when this session already forced a skew reload. */
export function skewReloadAlreadyAttempted(storage: Pick<Storage, 'getItem'>): boolean {
  try {
    return storage.getItem(BUNDLE_SKEW_RELOAD_KEY) === '1';
  } catch {
    return false;
  }
}

export function markSkewReloadAttempted(storage: Pick<Storage, 'setItem'>): void {
  try {
    storage.setItem(BUNDLE_SKEW_RELOAD_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function clearSkewReloadAttempt(storage: Pick<Storage, 'removeItem'>): void {
  try {
    storage.removeItem(BUNDLE_SKEW_RELOAD_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Compare running frontend_git_sha to /version.json.
 * On mismatch: beacon once, then exactly one cache-busted reload per session.
 */
export async function checkAndHealBundleSkew(
  deps: BundleSkewDeps,
): Promise<BundleSkewResult> {
  const remote = await deps.fetchRemote();
  if (!remote) return 'offline';

  const current = remoteFrontendSha(remote);
  const running = (deps.runningSha || '').trim();
  if (bundleShaMatches(running, current)) return 'matched';

  if (!beaconAlreadyEmitted(deps.storage, running, current)) {
    markBeaconEmitted(deps.storage, running, current);
    const emit = deps.emitBeacon || emitBundleSkewDetectedBeacon;
    emit(running, current, {
      current_bundle: remote.bundle_hash || null,
    });
  }

  if (reloadIssued || skewReloadAlreadyAttempted(deps.storage)) return 'bounded';

  reloadIssued = true;
  if (await shouldDeferBundleSkewReloadForPlayback(running, current)) {
    reloadIssued = false;
    return 'bounded';
  }

  markSkewReloadAttempted(deps.storage);
  deps.reload();
  return 'reloaded';
}

export function defaultBundleSkewDeps(): BundleSkewDeps | null {
  if (typeof window === 'undefined') return null;
  const meta = getWebBundleMeta();
  return {
    runningSha: meta.sha || meta.gitHash || '',
    fetchRemote: fetchRemoteVersion,
    storage: window.sessionStorage,
    reload: () => {
      markInAppNavigationReloadPending();
      cacheBustedReload();
    },
    emitBeacon: (running, current, extra) => {
      emitBundleSkewDetectedBeacon(running, current, {
        ...extra,
        running_bundle: meta.bundleHash || null,
      });
    },
  };
}

let reloadIssued = false;

export function resetBundleSkewReloadLatchForTests(): void {
  reloadIssued = false;
}

export { installBundleSkewGuard, resetBundleSkewGuardForTests } from './bundleSkewGuardInstall';
